import { useEffect, useState } from 'react';
import { Maximize2, Minimize2 } from 'lucide-react';
import './VideoMaximize.css';

/*
 * The maximize button every video on the brochure carries (meeting 2026-10-20:
 * "add a maximize button to all video frames"). Pass the <video> element (a ref's
 * .current, or state set from a callback ref) and a static Trapezium id
 * (`<screen>.maximize-<video>`). Fullscreen goes to the video itself; iOS Safari,
 * which has no element fullscreen, falls back to its native video player.
 *
 * Position it inside the video's frame (the frame needs position: relative);
 * `className` lets a page nudge it clear of its own controls.
 */
const fullscreenElement = () => document.fullscreenElement || document.webkitFullscreenElement || null;

export default function VideoMaximize({ video, id, label = 'the video', className = '' }) {
  const [full, setFull] = useState(false);

  useEffect(() => {
    const sync = () => setFull(Boolean(video) && fullscreenElement() === video);
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, [video]);

  const toggle = () => {
    if (!video) return;
    if (fullscreenElement()) {
      (document.exitFullscreen || document.webkitExitFullscreen).call(document);
      return;
    }
    if (!video.src && video.dataset.src) video.src = video.dataset.src;
    if (video.requestFullscreen) video.requestFullscreen().catch(() => {});
    else if (video.webkitRequestFullscreen) video.webkitRequestFullscreen();
    else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
    if (video.paused) video.play().catch(() => {});
  };

  const Icon = full ? Minimize2 : Maximize2;
  return (
    <button
      type="button"
      className={`vmax ${className}`.trim()}
      onClick={toggle}
      aria-pressed={full}
      aria-label={full ? `Exit full screen for ${label}` : `Maximize ${label}`}
      data-trapezium-id={id}
    >
      <Icon size={16} aria-hidden="true" />
    </button>
  );
}
